/**
 * Schedule lightbox: wheel zoom, click zoom, drag when zoomed (movement threshold),
 * double-click reset. Modal overflow:hidden preserves wheel capture.
 */
(function () {
  const MIN_SCALE = 1;
  const MAX_SCALE = 6;
  const CLICK_ZOOM_STEP = 0.45;
  const DRAG_THRESHOLD = 8;

  function resetTransform(modal, pan, viewport) {
    pan.style.transform = '';
    pan.dataset.scale = '1';
    pan.dataset.tx = '0';
    pan.dataset.ty = '0';
    viewport.classList.remove('is-zoomed', 'is-dragging');
  }

  function applyTransform(pan, viewport) {
    const scale = parseFloat(pan.dataset.scale || '1');
    const tx = parseFloat(pan.dataset.tx || '0');
    const ty = parseFloat(pan.dataset.ty || '0');
    pan.style.transform = `translate(${tx}px, ${ty}px) scale(${scale})`;
    if (scale > 1.001) {
      viewport.classList.add('is-zoomed');
    } else {
      viewport.classList.remove('is-zoomed');
    }
  }

  function wheelScaleDelta(e) {
    let dy = e.deltaY;
    if (e.deltaMode === 1) dy *= 16;
    if (e.deltaMode === 2) dy *= window.innerHeight || 600;
    const sign = dy > 0 ? -1 : 1;
    const step = Math.min(0.28, 0.035 + Math.abs(dy) * 0.0018);
    return sign * step;
  }

  function initScheduleModal(modal) {
    if (modal.dataset.sacbeScheduleText === 'true') return;
    if (modal.dataset.sacbeScheduleZoomInit === 'true') return;
    modal.dataset.sacbeScheduleZoomInit = 'true';

    const viewport = modal.querySelector('[data-sacbe-schedule-viewport]');
    const pan = modal.querySelector('[data-sacbe-schedule-pan]');
    if (!viewport || !pan) return;

    pan.dataset.scale = '1';
    pan.dataset.tx = '0';
    pan.dataset.ty = '0';

    let dragActive = false;
    /** True if this pointer gesture panned or pinched (suppresses click-to-zoom). */
    let gestureDidPan = false;
    let ptrDown = false;
    let startX = 0;
    let startY = 0;
    let origTx = 0;
    let origTy = 0;
    const pointers = new Map();
    let pinchDist = 0;
    let pinchScale = 1;

    function pointerSpan() {
      const pts = Array.from(pointers.values());
      if (pts.length < 2) return 0;
      return Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y);
    }

    function applyClickZoom() {
      const scale = parseFloat(pan.dataset.scale || '1');
      let next;
      if (scale >= MAX_SCALE - 0.05) {
        next = Math.max(MIN_SCALE, scale - CLICK_ZOOM_STEP);
      } else {
        next = Math.min(MAX_SCALE, scale + CLICK_ZOOM_STEP);
      }
      pan.dataset.scale = String(next);
      if (next <= MIN_SCALE + 0.001) {
        pan.dataset.tx = '0';
        pan.dataset.ty = '0';
      }
      applyTransform(pan, viewport);
    }

    function onWheel(e) {
      if (!modal.hasAttribute('open')) return;
      const t = e.target;
      if (!viewport.contains(t) && t !== viewport) return;
      e.preventDefault();
      e.stopPropagation();
      const scale = parseFloat(pan.dataset.scale || '1');
      let next = Math.min(MAX_SCALE, Math.max(MIN_SCALE, scale + wheelScaleDelta(e)));
      pan.dataset.scale = String(next);
      if (next <= MIN_SCALE + 0.001) {
        pan.dataset.tx = '0';
        pan.dataset.ty = '0';
      }
      applyTransform(pan, viewport);
    }

    viewport.addEventListener('wheel', onWheel, { passive: false, capture: true });

    viewport.addEventListener(
      'pointerdown',
      function (e) {
        if (!modal.hasAttribute('open') || e.button !== 0) return;
        if (!viewport.contains(e.target)) return;
        pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
        ptrDown = true;
        dragActive = false;
        gestureDidPan = false;
        startX = e.clientX;
        startY = e.clientY;
        origTx = parseFloat(pan.dataset.tx || '0');
        origTy = parseFloat(pan.dataset.ty || '0');
        if (pointers.size === 2) {
          pinchDist = pointerSpan();
          pinchScale = parseFloat(pan.dataset.scale || '1');
          gestureDidPan = true;
        }
        try {
          viewport.setPointerCapture(e.pointerId);
        } catch (_) {}
      },
      true
    );

    viewport.addEventListener(
      'pointermove',
      function (e) {
        if (!pointers.has(e.pointerId)) return;
        pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
        if (pointers.size >= 2 && pinchDist > 0) {
          const next = Math.min(MAX_SCALE, Math.max(MIN_SCALE, pinchScale * (pointerSpan() / pinchDist)));
          pan.dataset.scale = String(next);
          if (next <= MIN_SCALE + 0.001) {
            pan.dataset.tx = '0';
            pan.dataset.ty = '0';
          }
          gestureDidPan = true;
          applyTransform(pan, viewport);
          return;
        }
        if (!ptrDown || pointers.size !== 1) return;
        const scale = parseFloat(pan.dataset.scale || '1');
        const dx = e.clientX - startX;
        const dy = e.clientY - startY;
        if (!dragActive) {
          if (scale <= 1.001) return;
          if (Math.abs(dx) < DRAG_THRESHOLD && Math.abs(dy) < DRAG_THRESHOLD) return;
          dragActive = true;
          gestureDidPan = true;
          viewport.classList.add('is-dragging');
        }
        pan.dataset.tx = String(origTx + dx);
        pan.dataset.ty = String(origTy + dy);
        applyTransform(pan, viewport);
      },
      true
    );

    function endPointer(e) {
      pointers.delete(e.pointerId);
      if (pointers.size < 2) pinchDist = 0;
      if (pointers.size === 0) ptrDown = false;
      viewport.classList.remove('is-dragging');
      try {
        viewport.releasePointerCapture(e.pointerId);
      } catch (_) {}
      dragActive = false;
    }

    viewport.addEventListener('pointerup', endPointer, true);
    viewport.addEventListener('pointercancel', endPointer, true);

    viewport.addEventListener(
      'click',
      function (e) {
        if (!modal.hasAttribute('open')) return;
        if (e.detail !== 1) return;
        if (!viewport.contains(e.target)) return;
        if (gestureDidPan) return;
        const dx = Math.abs(e.clientX - startX);
        const dy = Math.abs(e.clientY - startY);
        if (dx > DRAG_THRESHOLD || dy > DRAG_THRESHOLD) return;
        applyClickZoom();
      },
      true
    );

    viewport.addEventListener(
      'dblclick',
      function () {
        if (!modal.hasAttribute('open')) return;
        resetTransform(modal, pan, viewport);
      },
      true
    );

    const mo = new MutationObserver(function () {
      if (!modal.hasAttribute('open')) {
        resetTransform(modal, pan, viewport);
      }
    });
    mo.observe(modal, { attributes: true, attributeFilter: ['open'] });
  }

  function boot() {
    document.querySelectorAll('[data-sacbe-schedule-modal]').forEach(initScheduleModal);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', boot);
})();
